"use client";

import { useEffect, useMemo, useState } from "react";
import useGetApplication from "./useGetApplication";
import { getCookieValue, removeAllCookies } from "@/utils/cookies";
import useGetFormDetails from "./useGetFormDetails";
import { buildFormToLoad } from "@/utils/Form/formatData";
import { getActiveServiceIds } from "@/utils/Form/formServices";
import LocalStorageUtils from "@/utils/localStorageUtils";

const usePrepareFormData = ({ pdfId }: { pdfId: string | null }) => {
  const { applicationId, getApplicationInfo } = useGetApplication();
  const [orderFromApi, setOrderFromApi] = useState<Record<string, any>>();
  const [formDetails, setFormDetails] = useState<Record<string, any>>();

  const [servicesDetails, setServicesDetails] = useState<Record<string, any>[]>([]);
  const { getFormDetails } = useGetFormDetails();
  const [loading, setLoading] = useState<boolean>(true);
  const [formHasLoadedData, setFormHasLoadedData] = useState<boolean>(true);

  const orderCookie = useMemo(() => {
    const cookie = getCookieValue("address_token");
    return {
      ...cookie,
      pdfId: cookie?.pdfId,
      orderReference: cookie?.reference,
      applicationId: applicationId,
    };
  }, [applicationId]);

  const verifyApplicationStatus = async () => {
    try {
      await getApplicationInfo();
    } catch (error: any) {
      if (typeof error.applicationStatus === "string" && error.applicationStatus !== "registered") {
        removeAllCookies();
        window.location.href = window.location.pathname;
      }
      throw error;
    }
  };

  const loadFormData = async () => {
    try {
      if (orderCookie.applicationId && orderCookie.pdfId === pdfId) {
        await verifyApplicationStatus();
      }

      let formDetailsResponse =
        pdfId &&
        (await getFormDetails({
          pdfId,
          applicationId: orderCookie.pdfId === pdfId ? applicationId : null,
        }));

      if (formDetailsResponse && formDetailsResponse?.pdf?.name) {
        // Get the active service ids for the form
        const activeServiceIds = getActiveServiceIds(formDetailsResponse.pdf.name);

        // If there are active service ids, get the services data from API
        if (activeServiceIds.length > 0) {
          const servicesData = await Promise.all(
            activeServiceIds.map(async (serviceId) => {
              try {
                const serviceDetails = await getFormDetails({
                  pdfId: serviceId,
                  applicationId: undefined,
                });
                return {
                  pdf: serviceDetails?.pdf,
                  price: serviceDetails?.price,
                  total: serviceDetails?.total,
                };
              } catch (error) {
                console.error(`Error loading service ${serviceId}:`, error);
                return null;
              }
            })
          );

          // Filter out null values (failed requests)
          const validServices = servicesData.filter((s): s is NonNullable<typeof s> => s !== null);
          setServicesDetails(validServices);
        }
      }

      if (formDetailsResponse) {
        const parsedFormDetails = buildFormToLoad(formDetailsResponse.form as Record<string, any>);

        setOrderFromApi({
          ...parsedFormDetails,
          addons: formDetailsResponse.addons,
        });

        setFormDetails({
          addons: formDetailsResponse.addons,
          pdf: formDetailsResponse.pdf,
          price: formDetailsResponse.price,
          total: formDetailsResponse.total,
        });

        const formIsNotEmpty = Object.keys(parsedFormDetails).length !== 0;
        setFormHasLoadedData(formIsNotEmpty);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);

      console.error(error);
      throw error;
    }
  };

  useEffect(() => {
    pdfId && loadFormData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfId]);

  useEffect(() => {
    // Save formDetails data to localStorage
    if (formDetails) LocalStorageUtils.setItem("FORM_DETAILS", formDetails);
  }, [formDetails]);

  return {
    loading,
    orderFromApi,
    formHasLoadedData,
    formDetails,
    servicesDetails,
  };
};

export default usePrepareFormData;
